/** @jsxRuntime classic */
/** @jsx jsx */
import React, { Fragment, useMemo } from 'react';
import { IExpandedSlot, IPopupActions, ISlot, isExternalSlotType, MaintenanceTimingEnum, SlotTypeEnum } from '../types';
import { Dot, Typography } from '../../../atoms';
import { css, jsx } from '@emotion/react';
import { ETypography, ETypographyColor, IColors, Icons } from '../../../types';
import { useColors } from '../../../hooks/useColors';
import { BnIcon } from '../../../components/icons_v2';
import { flexWrapCss } from '../../../styles/utils';
import { ETimeFormat, diffCalculationFromDateTime, formatTime, isSameDate } from '../../../calendar/utils/timeUtils';
import { Dash } from '../../../components/Dash';
import { Tooltip } from '../../../components/Tooltip';
import { ClampOrTruncateWithTooltip } from '../../../molecules/ClampOrTruncateTextWithTooltip';
import { Button } from '../../../components/Button';
import { SizeEnum } from '../../../lib/constants';
import { ViewFormsAction } from './ViewFormsAction';

const truncateDiv = css`
	div {
		display: block;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

const noFlexWrapCss = css`
	display: flex;
	white-space: nowrap;
	overflow: hidden;
`;

const noFlexShrinkCss = css`
	flex-shrink: 0;
	align-items: flex-start;
	white-space: nowrap !important;
`;

export const mainBodyLineCss = (colors: IColors) => css`
	display: flex;
	flex-direction: row;
	align-items: center;
	max-width: 100%;
	margin: 0.7rem 0rem;
	gap: 0.5rem;
	svg {
		color: ${colors.tx_text_primary};
		width: 1.7rem;
		height: 1.7rem;
		flex: 0 0 auto;
	}
`;

const textLineCss = css`
	display: flex;
	flex-direction: row;
	align-items: center;
	max-width: 100%;
	gap: 0.3rem;
`;

const maintenaceTitleCss = css`
	display: block !important;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	align-items: flex-start;
`;

const bodyCss = css`
	padding: 1rem 2rem;
	display: flex;
	flex-direction: column;
`;

export interface IMainBodyLabels {
	duration: (duration: string) => string;
	viewMoreMaintenace: (
		title: string,
		maintenanceTiming: string,
		startDate: string,
		startTime: string,
		durationValue: string,
		durationUnit: string
	) => string;
	viewMoreButton: (count: string | number) => string;
	maintenanceTiming: {
		AFTER: string;
		BEFORE: string;
		AT_THE_BEGINING: string;
		AT_THE_END: string;
	};
	durationUnitTypes: {
		weeks: string;
		minutes: string;
		hours: string;
		days: string;
	};
	editNotes: string;
	addNotes: string;
	viewForms: string;
}

const sortSlotsByStart = (slots: ISlot[]): ISlot[] => {
	return slots.sort((slot1, slot2) => {
		const dateTimeA = new Date(`${slot1.startDate.split('T')[0]}T${slot1.startTime}.000Z`).getTime();
		const dateTimeB = new Date(`${slot2.startDate.split('T')[0]}T${slot2.startTime}.000Z`).getTime();
		return dateTimeA - dateTimeB;
	});
};

const TruncatedBodyLine = ({ 
	text,
	icon,
	colors
}: { 
	text: string; 
	icon: Icons,
	colors: IColors
}) => {
	const mainBodyLineStyle = useMemo(() => mainBodyLineCss(colors), [colors]);
	return (
		<div css={[mainBodyLineStyle, truncateDiv]}>
			<BnIcon icon={icon} />
			<Typography color={ETypographyColor.primary} type={ETypography.body2Accented}>
				{text}
			</Typography>
		</div>
	);
};

const Customer = ({
	expandedSlot,
	colors
} : {
	expandedSlot: IExpandedSlot,
	colors: IColors
}) => {
	return isExternalSlotType(expandedSlot?.slotType) &&
		(expandedSlot?.customer?.[0]?.firstName || expandedSlot?.customer?.[0]?.lastName) ? (
		<TruncatedBodyLine
			text={`${expandedSlot.customer[0].firstName} ${expandedSlot.customer[0].lastName}`}
			icon={Icons.profile_outline}
			colors={colors}
			data-aid="popupBody-customer"
		/>
	) : null;
};

const TimeFrame = ({
	expandedSlot,
	colors,
	labels
} : {
	expandedSlot: IExpandedSlot,
	colors: IColors,
	labels: IMainBodyLabels
}) => {
	return expandedSlot?.startDate && expandedSlot?.endDate && expandedSlot?.startTime && expandedSlot?.endTime ? (
		<div data-aid="popupBody-timeFrame" css={[mainBodyLineCss(colors)]}>
			<BnIcon icon={Icons.clock} />
			<div css={[textLineCss, flexWrapCss]}>
				<Typography color={ETypographyColor.primary} type={ETypography.body2Accented}>
					{formatTime(expandedSlot.startDate, ETimeFormat.SHORT_DATE)}
				</Typography>
				<Dot color={colors.tx_text_primary} />
				{isSameDate(expandedSlot.startDate, expandedSlot.endDate) ? (
					<div css={[textLineCss, noFlexWrapCss]}>
						<Typography color={ETypographyColor.primary} type={ETypography.body2}>
							{formatTime(expandedSlot.startTime, ETimeFormat.TIME_12H_NO_SPACE, ETimeFormat.TIME_24H)}
						</Typography>
						<Dash color={ETypographyColor.primary} type={ETypography.body2} />
						<Typography color={ETypographyColor.primary} type={ETypography.body2}>
							{formatTime(expandedSlot.endTime, ETimeFormat.TIME_12H_NO_SPACE, ETimeFormat.TIME_24H)}
						</Typography>
					</div>
				) : (
					<Fragment>
						<Typography color={ETypographyColor.primary} type={ETypography.body2}>
							{formatTime(expandedSlot.startTime, ETimeFormat.TIME_12H_NO_SPACE, ETimeFormat.TIME_24H)}
						</Typography>
						<Dash color={ETypographyColor.primary} type={ETypography.body2Accented} />
						<Typography color={ETypographyColor.primary} type={ETypography.body2Accented}>
							{formatTime(expandedSlot.endDate, ETimeFormat.SHORT_DATE)}
						</Typography>
						<Dot color={colors.tx_text_primary} />
						<Typography color={ETypographyColor.primary} type={ETypography.body2}>
							{formatTime(expandedSlot.endTime, ETimeFormat.TIME_12H_NO_SPACE, ETimeFormat.TIME_24H)}
						</Typography>
					</Fragment>
				)}
				<Dot color={colors.tx_text_primary} />
				<Typography color={ETypographyColor.secondary} type={ETypography.body2}>
					{labels.duration(
						diffCalculationFromDateTime(
							expandedSlot.startDate,
							expandedSlot.startTime,
							expandedSlot.endDate,
							expandedSlot.endTime
						)
					)}
				</Typography>
			</div>
		</div>
	) : null;
};

const Location = ({
	expandedSlot,
	colors
} : {
	expandedSlot: IExpandedSlot,
	colors: IColors
}) => {
	return expandedSlot?.resource?.name ? (
		<TruncatedBodyLine 
			text={expandedSlot.resource.name} 
			icon={Icons.location} 
			colors={colors} 
			data-aid="popupBody-location" 
		/>
	) : null;
};

const Product = ({
	expandedSlot,
	colors
} : {
	expandedSlot: IExpandedSlot,
	colors: IColors
}) => {
	return expandedSlot?.productUser?.productName ? (
		<TruncatedBodyLine 
			text={expandedSlot.productUser.productName} 
			icon={Icons.bag} 
			colors={colors} 
			data-aid="popupBody-product" 
		/>
	) : null;
};

const MaintenanceTooltipContent = ({
	expandedSlot,
	labels
} : {
	expandedSlot: IExpandedSlot,
	labels: IMainBodyLabels
}) => {
	if (!expandedSlot?.maintenanceSlots) return null;
	const sortedSlots = sortSlotsByStart(expandedSlot.maintenanceSlots);

	return (
		<Fragment>
			{sortedSlots.map(slot => {
				if (!slot.title) return null;

				const timing = labels.maintenanceTiming[MaintenanceTimingEnum[slot.maintenanceTiming]] ?? '';
				const shortDate = formatTime(slot.startDate, ETimeFormat.SHORT_DATE) ?? '';
				const timeRange = formatTime(slot.startTime, ETimeFormat.TIME_12H_NO_SPACE, ETimeFormat.TIME_24H) ?? '';
				const duration = slot.durationValue?.toString() ?? '';
				const unit = labels.durationUnitTypes[slot.maintenanceDurationdurationType] ?? '';

				const label = labels.viewMoreMaintenace(
					slot.title,
					timing,
					shortDate,
					timeRange,
					duration,
					unit
				);

				return (
					<Typography key={slot.title} color={ETypographyColor.primary} type={ETypography.caption}>
						{label}
					</Typography>
				);
			})}
		</Fragment>
	);
};

const ViewMoreMaintenance = ({
	expandedSlot,
	labels
} : {
	expandedSlot: IExpandedSlot,
	labels: IMainBodyLabels
}) => {
	if (!expandedSlot?.maintenanceSlots) return null;
	const viewMoreLabel = labels.viewMoreButton(expandedSlot.maintenanceSlots.length - 1);

	return (
		<Tooltip content={<MaintenanceTooltipContent expandedSlot={expandedSlot} labels={labels}/>} isTopLayer={true}>
			<Typography
				data-aid="viewMoreButton"
				color={ETypographyColor.secondary}
				type={ETypography.overlineLink}
				css={noFlexShrinkCss}
				aria-label="View more maintenance slots"
			>
				{viewMoreLabel}
			</Typography>
		</Tooltip>
	);
};

const Maintenance = ({
	expandedSlot,
	colors,
	labels
} : {
	expandedSlot: IExpandedSlot,
	colors: IColors,
	labels: IMainBodyLabels;
}) => {
	const firstMaintenanceSlot = expandedSlot?.maintenanceSlots?.length ? sortSlotsByStart(expandedSlot.maintenanceSlots)[0] : undefined;
	const showMaintenance = firstMaintenanceSlot?.title !== undefined;
	if (!showMaintenance) return null;

	const {
		title,
		startTime,
		durationValue,
		maintenanceTiming,
		maintenanceDurationdurationType
	} = firstMaintenanceSlot;

	const timing = labels.maintenanceTiming[MaintenanceTimingEnum[maintenanceTiming]] ?? '';
	const timeRange = formatTime(startTime, ETimeFormat.TIME_12H_NO_SPACE, ETimeFormat.TIME_24H) ?? '';
	const unit = labels.durationUnitTypes[maintenanceDurationdurationType] ?? '';
	const duration = labels.duration(`${durationValue} ${unit}`) ?? '';

	const hasAdditionalSlots = expandedSlot.maintenanceSlots && expandedSlot?.maintenanceSlots?.length > 1;

	return showMaintenance ? (
		<div data-aid="popupBody-maintenance" css={[mainBodyLineCss(colors)]}>
			<BnIcon icon={Icons.maintenance} />
			<div css={textLineCss}>
				<Typography color={ETypographyColor.primary} type={ETypography.body2Accented} css={maintenaceTitleCss}>
					{title}
				</Typography>
				<Dot color={colors.tx_text_primary} />
				<Typography color={ETypographyColor.primary} type={ETypography.body2} css={noFlexShrinkCss}>
					{timing}
				</Typography>
				<Dot color={colors.tx_text_primary} />
				<Typography color={ETypographyColor.primary} type={ETypography.body2} css={noFlexShrinkCss}>
					{timeRange}
				</Typography>
				<Dot color={colors.tx_text_primary} />
				<Typography color={ETypographyColor.secondary} type={ETypography.body2} css={noFlexShrinkCss}>
					{duration}
				</Typography>
				{hasAdditionalSlots && <ViewMoreMaintenance expandedSlot={expandedSlot} labels={labels}/>}
			</div>
		</div>
	) : null;
};

const NotesBodyLine = ({ 
	text, 
	icon,
	colors
}: { 
	text: string; 
	icon: Icons,
	colors: IColors
}) => {
	const mainBodyLineStyle = useMemo(() => mainBodyLineCss(colors), [colors]);
	const charsPerLine = 60;
	const maxDisplayedLine = 3;
	return (
		<div css={mainBodyLineStyle}>
			<BnIcon icon={icon} />
			<ClampOrTruncateWithTooltip
				text={text}
				maxCharsPerLine={charsPerLine}
				maxDisplayedLines={maxDisplayedLine}
				data-aid="popupBody-notes-text"
			/>
		</div>
	);
};

const PrivateNotes = ({
	expandedSlot,
	colors
} : {
	expandedSlot: IExpandedSlot,
	colors: IColors
}) => {
	return expandedSlot?.privateNotes ? (
		<NotesBodyLine 
			text={expandedSlot.privateNotes} 
			icon={Icons.private_notes} 
			colors={colors} 
			data-aid="popupBody-privateNotes" 
		/>
	) : null;
};

const PublicNotes = ({
	expandedSlot,
	colors
} : {
	expandedSlot: IExpandedSlot,
	colors: IColors
}) => {
	return expandedSlot?.publicNotes ? (
		<NotesBodyLine 
			text={expandedSlot.publicNotes} 
			icon={Icons.public_notes} 
			colors={colors} 
			data-aid="popupBody-publicNotes" 
		/>
	) : null;
};

export const MainBody = ({
	expandedSlot,
	labels,
	actions,
}: {
	expandedSlot: IExpandedSlot;
	labels: IMainBodyLabels;
	actions?: IPopupActions;
}) => {
	const { colors } = useColors();
	const isExistingNotes = expandedSlot?.publicNotes || expandedSlot?.privateNotes;

	return (
		<div data-aid="popupBody-mainBody" css={bodyCss}>
			<Customer expandedSlot={expandedSlot} colors={colors}/>
			<TimeFrame expandedSlot={expandedSlot} colors={colors}  labels={labels}/>
			<Location expandedSlot={expandedSlot} colors={colors}/>
			<Product expandedSlot={expandedSlot} colors={colors}/>
			<Maintenance expandedSlot={expandedSlot} colors={colors}  labels={labels}/>
			<PrivateNotes expandedSlot={expandedSlot} colors={colors}/>
			<PublicNotes expandedSlot={expandedSlot} colors={colors}/>
			{actions?.editNotes?.allow && (
				<div css={mainBodyLineCss(colors)} data-aid="edit-notes">
					<BnIcon icon={isExistingNotes ? Icons.edit_outline : Icons.note} />
					<Button theme="basicLine" sizer={SizeEnum.EXTRA_SMALL} onClick={actions.editNotes?.handle}>
						<Typography color={ETypographyColor.secondary} type={ETypography.overlineLink}>
							{isExistingNotes ? labels.editNotes : labels.addNotes}
						</Typography>
					</Button>
				</div>
			)}
			<ViewFormsAction expandedSlot={expandedSlot} colors={colors} labels={labels} actions={actions} />
		</div>
	);
};
